import React from 'react';
import { LayoutGroup, motion } from 'framer-motion';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ColoredHighlightedText } from './colored/ColoredHighlightedText';
import { ExtractionSession } from '../types/multi-extraction';
import { ExtractionStatus } from '../types/multi-extraction';
import { CodeMetadataItem, CodeMetadataResult, CodeSystem, PcsFinalCode } from '../types/icd10';
import { Badge } from './ui/badge';
import { Button } from './animate-ui/components/buttons/button';
import { useExtractions } from '../hooks/useExtractions';
import { Spinner } from './ui/shadcn-io/spinner';
import { Collapsible } from './ui/collapsible';
import { AnimatedCollapsibleSection } from './ui/animated-collapsible-section';
import { CopyIcon } from './Icons';
import { useTranslation } from '../i18n';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { BsBookmarkStarFill } from 'react-icons/bs';
import {
  fetchAndCacheCodeMetadata,
  getCachedCodeMetadata,
  getMissingCodeMetadataItems,
  makeCodeMetadataKey,
} from '../services/codeMetadataCache';

type ResultsContentProps = {
  session: ExtractionSession | null;
  hideIrrelevant: boolean;
  hoveredCodes: string[] | null;
  selectedCodes?: string[] | null;
  setHideIrrelevant: (v: boolean) => void;
  setHoveredCodes: (c: string[] | null) => void;
  setSelectedCodes?: (c: string[] | null) => void;
};

export const ResultsContent: React.FC<ResultsContentProps> = ({ session, hideIrrelevant, hoveredCodes, selectedCodes, setHideIrrelevant, setHoveredCodes, setSelectedCodes }) => {
  if (!session) return null;

  const { t, locale } = useTranslation();
  const results = session.results;
  const extractions = useExtractions();

  const [localFeedback, setLocalFeedback] = React.useState<string | undefined>(session.feedback);
  const [savingFeedback, setSavingFeedback] = React.useState(false);
  const [codeMetadataMap, setCodeMetadataMap] = React.useState<Record<string, CodeMetadataResult>>({});
  const [isMetadataLoading, setIsMetadataLoading] = React.useState(false);
  const [metadataError, setMetadataError] = React.useState<string | null>(null);
  const lastMetadataKeyRef = React.useRef<string | null>(null);
  const codeItemRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const diagnosticsRan = results?.ran_diagnostic !== false;
  const pcsRan = !!results?.ran_pcs;
  const pcsFinalCodes = results?.pcs?.final_codes ?? [];
  const diagnosticCodeCount = results?.codes?.length ?? 0;
  const totalCodesFound = diagnosticCodeCount + pcsFinalCodes.length;
  const summarizedText = (results as any)?.metadata?.summarized_text as string | undefined;
  const prunedText = (results as any)?.metadata?.pruned_text as string | undefined;
  const processedText = prunedText || summarizedText;
  const normalizeMarkdownText = (value?: string) => {
    if (!value) return value;
    let normalized = value;
    normalized = normalized.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    if (normalized.includes('\\n') || normalized.includes('\\r')) {
      normalized = normalized
        .replace(/\\r\\n/g, '\n')
        .replace(/\\n/g, '\n')
        .replace(/\\r/g, '\n');
    }
    normalized = normalized.replace(/\u2028|\u2029/g, '\n');
    return normalized;
  };
  const normalizedProcessedText = normalizeMarkdownText(processedText);
  const hasProcessedText = !!processedText;
  const [showOriginalText, setShowOriginalText] = React.useState(false);
  const [showRawProcessedText, setShowRawProcessedText] = React.useState(false);
  const [highlightMode, setHighlightMode] = React.useState<'diagnosis' | 'pcs' | 'both'>('diagnosis');
  const [isDiagnosisListOpen, setIsDiagnosisListOpen] = React.useState(true);
  const [isPcsListOpen, setIsPcsListOpen] = React.useState(true);
  const [hoveredPcsCodes, setHoveredPcsCodes] = React.useState<string[] | null>(null);
  const [selectedPcsCodes, setSelectedPcsCodes] = React.useState<string[] | null>(null);
  const [pinnedCodes, setPinnedCodes] = React.useState<string[] | null>(null);
  const [pinnedPcsCodes, setPinnedPcsCodes] = React.useState<string[] | null>(null);
  const modeLabels: string[] = [];
  if (diagnosticsRan) {
    modeLabels.push(t('status.mode_diagnostic', 'Diagnostic'));
  }
  if (pcsRan) {
    modeLabels.push(t('status.mode_pcs', 'PCS'));
  }
  const modeSummary = modeLabels.length > 0 ? modeLabels.join(' + ') : t('status.mode_none', 'N/A');

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      console.warn('Copy failed', e);
    }
  };

  const handleHighlightClick = React.useCallback((codes: string[]) => {
    if (!codes || codes.length === 0) return;

    const diagnosisSet = new Set((results?.codes || []).map((c: any) => c.code).filter(Boolean));
    const pcsSet = new Set(
      (results?.pcs?.final_codes || [])
        .map((c: any) => c?.code7 || c?.code4)
        .filter(Boolean)
    );
    const diagnosisOnly = codes.filter((c) => diagnosisSet.has(c));
    const pcsOnly = codes.filter((c) => pcsSet.has(c));

    if (setSelectedCodes) {
      const unique = Array.from(new Set(diagnosisOnly));
      setSelectedCodes(unique.length > 0 ? unique : null);
    }

    const orderedCandidates = (results?.codes || [])
      .map((code: any) => code.code)
      .filter((code: string) => diagnosisOnly.includes(code));
    const fallback = diagnosisOnly.filter((code) => !orderedCandidates.includes(code));
    const orderedAll = [...orderedCandidates, ...fallback];
    setPinnedCodes(orderedAll.length > 0 ? orderedAll : null);

    // PCS selection is separate (so it doesn't interfere with diagnosis pinning logic).
    const pcsUnique = pcsOnly.length > 0 ? Array.from(new Set(pcsOnly)) : null;
    setSelectedPcsCodes(pcsUnique);

    const pcsOrderedCandidates = (results?.pcs?.final_codes || [])
      .map((c: any) => c?.code7 || c?.code4)
      .filter((code: string) => pcsOnly.includes(code));
    const pcsFallback = pcsOnly.filter((code) => !pcsOrderedCandidates.includes(code));
    const pcsOrderedAll = [...pcsOrderedCandidates, ...pcsFallback];
    setPinnedPcsCodes(pcsOrderedAll.length > 0 ? pcsOrderedAll : null);
  }, [setSelectedCodes, results?.codes, results?.pcs?.final_codes]);

  React.useEffect(() => {
    setLocalFeedback(session.feedback);
  }, [session.feedback, session.id]);

  React.useEffect(() => {
    setShowOriginalText(false);
  }, [session.id, hasProcessedText]);

  React.useEffect(() => {
    // Keep highlight mode per-session predictable.
    setHighlightMode('diagnosis');
  }, [session.id]);

  React.useEffect(() => {
    // Reset section visibility per-session.
    setIsDiagnosisListOpen(true);
    setIsPcsListOpen(true);
  }, [session.id]);

  React.useEffect(() => {
    // When highlighting PCS, collapse diagnosis list so more PCS codes fit on screen.
    if (!pcsRan) return;
    if (highlightMode === 'pcs') {
      setIsDiagnosisListOpen(false);
      setIsPcsListOpen(true);
    }
    if (highlightMode === 'both') {
      setIsDiagnosisListOpen(true);
      setIsPcsListOpen(true);
    }
  }, [highlightMode, pcsRan]);

  React.useEffect(() => {
    setHoveredPcsCodes(null);
    setSelectedPcsCodes(null);
  }, [session.id]);

  React.useEffect(() => {
    // PCS references frequently won't match diagnosis-processed text; ensure original text when highlighting PCS.
    if (!pcsRan) return;
    if (highlightMode === 'diagnosis') return;
    if (!hasProcessedText) return;
  }, [highlightMode, pcsRan, hasProcessedText]);

  React.useEffect(() => {
    if (!pinnedCodes || pinnedCodes.length === 0) return;
    if (selectedCodes && selectedCodes.some((code) => pinnedCodes.includes(code))) return;
    if (hoveredCodes && hoveredCodes.some((code) => pinnedCodes.includes(code))) return;
    setPinnedCodes(null);
  }, [pinnedCodes, selectedCodes, hoveredCodes]);

  React.useEffect(() => {
    if (!pinnedPcsCodes || pinnedPcsCodes.length === 0) return;
    if (selectedPcsCodes && selectedPcsCodes.some((code) => pinnedPcsCodes.includes(code))) return;
    if (hoveredPcsCodes && hoveredPcsCodes.some((code) => pinnedPcsCodes.includes(code))) return;
    setPinnedPcsCodes(null);
  }, [pinnedPcsCodes, selectedPcsCodes, hoveredPcsCodes]);

  React.useEffect(() => {
    setPinnedCodes(null);
    setPinnedPcsCodes(null);
  }, [session.id]);

  React.useEffect(() => {
    if (!session?.results) return;
    if (session.status !== ExtractionStatus.COMPLETED) return;

    const diagnosticCodes = (session.results?.codes || [])
      .map((code) => code.code)
      .filter((code): code is string => !!code);
    const pcsCodes = (session.results?.pcs?.final_codes || [])
      .map((code) => code.code7 || code.code4)
      .filter((code): code is string => !!code);

    const dedup = new Map<string, CodeMetadataItem>();
    diagnosticCodes.forEach((code) => {
      dedup.set(`diagnosis|${code}`, { code, system: 'diagnosis' });
    });
    pcsCodes.forEach((code) => {
      dedup.set(`pcs|${code}`, { code, system: 'pcs' });
    });

    const items = Array.from(dedup.values());
    if (items.length === 0) {
      setCodeMetadataMap({});
      return;
    }

    const requestKey = [
      session.id,
      (locale || '').toLowerCase(),
      items
        .map((item) => `${item.system}:${item.code}`)
        .sort()
        .join('|'),
    ].join('::');

    const cached = getCachedCodeMetadata(items, locale);
    setCodeMetadataMap(cached);

    const missing = getMissingCodeMetadataItems(items, locale);
    if (missing.length === 0) return;
    if (lastMetadataKeyRef.current === requestKey) return;

    lastMetadataKeyRef.current = requestKey;
    setIsMetadataLoading(true);
    setMetadataError(null);

    fetchAndCacheCodeMetadata(missing, locale)
      .then(() => {
        setCodeMetadataMap(getCachedCodeMetadata(items, locale));
      })
      .catch((error) => {
        console.warn('Failed to load code metadata', error);
        setMetadataError(error?.message || 'Failed to load code metadata');
      })
      .finally(() => {
        setIsMetadataLoading(false);
      });
  }, [session.id, session.status, session.results, locale]);

  const getMetadataFor = React.useCallback(
    (system: CodeSystem, code?: string) => {
      if (!code) return null;
      const key = makeCodeMetadataKey(system, code, locale);
      return codeMetadataMap[key] || null;
    },
    [codeMetadataMap, locale]
  );

  type HighlightCode = {
    code: string;
    description?: string;
    explanation?: string;
    confidence?: number;
    text_references?: any[];
    system?: 'diagnosis' | 'pcs';
  };

  const codesForHighlights = React.useMemo(() => {
    const codes = session.results?.codes || [];
    const diag: HighlightCode[] = codes.map((code) => {
      const meta = getMetadataFor('diagnosis', code.code);
      const title = meta?.data?.title as string | undefined;
      if (!title) return code;
      return { ...code, description: title, system: 'diagnosis' };
    });

    const diagTagged: HighlightCode[] = diag.map((c: any) => ({ ...c, system: 'diagnosis' }));

    const pcs: HighlightCode[] = (session.results?.pcs?.final_codes || [])
      .map((pcsCode) => {
        const pcsValue = pcsCode.code7 || pcsCode.code4;
        if (!pcsValue) return null;
        const meta = getMetadataFor('pcs', pcsValue);
        const title = meta?.data?.display_label as string | undefined;
        return {
          code: pcsValue,
          description: title,
          explanation: pcsCode.rationale || title,
          confidence: (pcsCode as any).confidence,
          text_references: (pcsCode as any).text_references,
          system: 'pcs',
        } as any;
      })
      .filter((c): c is any => !!c);

    if (highlightMode === 'pcs') return pcs;
    if (highlightMode === 'both') return [...diagTagged, ...pcs];
    return diagTagged;
  }, [session.results?.codes, session.results?.pcs?.final_codes, getMetadataFor, highlightMode]);

  const displayedText = showOriginalText ? session.originalText : (normalizedProcessedText || session.originalText);
  const rawProcessedText = processedText || '';

  // If processed (pruned/summarized) text exists, show it by default for all modes.
  // PCS agent may use pruned text now, so PCS text references should match this view.
  const highlightText = displayedText;
  const enableMarkdown = !showOriginalText && !!normalizedProcessedText;

  const onHighlightHoverSplit = React.useCallback((codes: string[] | null) => {
    if (!codes || codes.length === 0) {
      setHoveredCodes(null);
      setHoveredPcsCodes(null);
      return;
    }
    const diagnosisSet = new Set((results?.codes || []).map((c: any) => c.code).filter(Boolean));
    const pcsSet = new Set(
      (results?.pcs?.final_codes || [])
        .map((c: any) => c?.code7 || c?.code4)
        .filter(Boolean)
    );

    const diagnosisOnly = codes.filter((c) => diagnosisSet.has(c));
    const pcsOnly = codes.filter((c) => pcsSet.has(c));

    setHoveredCodes(diagnosisOnly.length > 0 ? diagnosisOnly : null);
    setHoveredPcsCodes(pcsOnly.length > 0 ? pcsOnly : null);
  }, [results?.codes, results?.pcs?.final_codes, setHoveredCodes]);

  const highlightHoveredCodes = React.useMemo(() => {
    const combined = [...(hoveredCodes || []), ...(hoveredPcsCodes || [])];
    return combined.length > 0 ? Array.from(new Set(combined)) : null;
  }, [hoveredCodes, hoveredPcsCodes]);

  const highlightSelectedCodes = React.useMemo(() => {
    const combined = [...(selectedCodes || []), ...(selectedPcsCodes || [])];
    return combined.length > 0 ? Array.from(new Set(combined)) : null;
  }, [selectedCodes, selectedPcsCodes]);

  const isCssColor = (v?: string) => {
    if (!v) return false;
    const s = v.trim();
    if (s.startsWith('#')) return /^#[0-9A-Fa-f]{3,8}$/.test(s);
    if (s.startsWith('rgb') || s.startsWith('hsl')) return true;
    const keywords = ['transparent','currentColor','black','white','red','blue','green','yellow','gray'];
    if (keywords.includes(s.toLowerCase())) return true;
    return false;
  };

  const isTailwindClass = (v?: string) => {
    if (!v) return false;
    return /(^|\s)(bg-|dark:|text-|ring-|from-|via-|to-)/.test(v);
  };

  // Deterministic color (used as a fallback when session.codeColors is missing a key).
  const hashToHue = (s: string) => {
    let hash = 0;
    for (let i = 0; i < s.length; i++) {
      hash = (hash << 5) - hash + s.charCodeAt(i);
      hash |= 0;
    }
    return Math.abs(hash) % 360;
  };

  const fallbackCssColorForCode = (code?: string) => {
    const c = String(code || '').trim();
    if (!c) return undefined;
    const hue = hashToHue(c);
    return `hsl(${hue}, 65%, 72%)`;
  };

  const skeletonTheme = {
    baseColor: 'hsl(var(--foreground) / 0.08)',
    highlightColor: 'hsl(var(--foreground) / 0.16)'
  };

  const pinnedCodeList = React.useMemo(() => {
    if (!pinnedCodes || pinnedCodes.length === 0) return [];
    const codes = results?.codes || [];
    const byCode = new Map(codes.map((code: any) => [code.code, code]));
    return pinnedCodes
      .map((code) => byCode.get(code))
      .filter((code): code is any => !!code);
  }, [results?.codes, pinnedCodes]);

  const unpinnedCodeList = React.useMemo(() => {
    const codes = results?.codes || [];
    if (!pinnedCodes || pinnedCodes.length === 0) return codes;
    const pinnedSet = new Set(pinnedCodes);
    return codes.filter((code: any) => !pinnedSet.has(code.code));
  }, [results?.codes, pinnedCodes]);

  const pinnedPcsCodeList = React.useMemo(() => {
    if (!pinnedPcsCodes || pinnedPcsCodes.length === 0) return [];
    const byCode = new Map(
      (pcsFinalCodes || [])
        .map((c: PcsFinalCode) => {
          const key = c.code7 || c.code4;
          return key ? [key, c] : null;
        })
        .filter(Boolean) as Array<[string, PcsFinalCode]>
    );
    return pinnedPcsCodes
      .map((code) => byCode.get(code))
      .filter((code): code is PcsFinalCode => !!code);
  }, [pcsFinalCodes, pinnedPcsCodes]);

  const unpinnedPcsCodeList = React.useMemo(() => {
    const codes = pcsFinalCodes || [];
    if (!pinnedPcsCodes || pinnedPcsCodes.length === 0) return codes;
    const pinnedSet = new Set(pinnedPcsCodes);
    return codes.filter((c: PcsFinalCode) => {
      const key = c.code7 || c.code4;
      return !key || !pinnedSet.has(key);
    });
  }, [pcsFinalCodes, pinnedPcsCodes]);

  const renderCodeCard = (code: any) => {
    const isActive = !!(hoveredCodes && hoveredCodes.includes(code.code));
    const isSelected = !!(selectedCodes && selectedCodes.includes(code.code));
    const outerCls = `group rounded-md overflow-hidden transition-colors duration-150 border-2 ${isSelected ? 'border-primary bg-card/95' : isActive ? 'border-primary bg-card/95' : 'border-transparent bg-[#f4f7fb] dark:bg-muted/25'}`;
    const meta = getMetadataFor('diagnosis', code.code);
    const title = meta?.data?.title as string | undefined;
    const shouldShowSkeleton = isMetadataLoading && !title && !metadataError;
    const mainSelectedCode = (results as any)?.metadata?.main_code_selection?.selected_code as string | undefined;
    const isMainCode = !!mainSelectedCode && mainSelectedCode.toUpperCase() === String(code.code || '').toUpperCase();
    return (
      <motion.div
        key={code.code}
        layoutId={`code-${code.code}`}
        className={outerCls}
        ref={(el) => {
          if (code.code) {
            codeItemRefs.current[code.code] = el;
          }
        }}
        onClick={() => {
          if (!setSelectedCodes) return;
          if (isSelected) {
            // deselect
            const next = (selectedCodes || []).filter((c) => c !== code.code);
            setSelectedCodes(next.length > 0 ? next : null);
          } else {
            const next = Array.from(new Set([...(selectedCodes || []), code.code]));
            setSelectedCodes(next);
          }
        }}
        layout="position"
        transition={{ type: 'spring', stiffness: 320, damping: 30 }}
      >
        <Collapsible
          title={
            <div
              className="grid grid-cols-[auto,1fr] gap-2 items-start min-w-0"
              onMouseEnter={(e) => {
                if (e.buttons === 1 || window.getSelection()?.type === 'Range') return;
                setHoveredCodes([code.code]);
              }}
              onMouseLeave={(e) => {
                if (e.buttons === 1 || window.getSelection()?.type === 'Range') return;
                setHoveredCodes(null);
              }}
            >
              {(() => {
                const raw = session.codeColors?.[code.code];
                if (isCssColor(raw)) {
                  return (
                    <div className="flex flex-col items-center space-y-3">
                      <span className="mt-0.5 inline-block w-2.5 h-2.5 rounded opacity-80" style={{ backgroundColor: raw }} />
                      {isMainCode && <BsBookmarkStarFill className="mt-1 text-primary" size={12} title={t('status.primary_code', 'Primary code')} />}
                    </div>
                  );
                }
                if (raw && isTailwindClass(raw)) {
                  return (
                    <div className="flex flex-col items-center">
                      <span className={`mt-0.5 inline-block w-2.5 h-2.5 rounded opacity-80 ${raw}`} />
                      {isMainCode && <BsBookmarkStarFill className="mt-1 text-primary" size={12} title={t('status.primary_code', 'Primary code')} />}
                    </div>
                  );
                }

                const fallback = fallbackCssColorForCode(code.code);
                return (
                  <div className="flex flex-col items-center">
                    <span className="mt-0.5 inline-block w-2.5 h-2.5 rounded opacity-80" style={fallback ? { backgroundColor: fallback } : undefined} />
                    {isMainCode && <BsBookmarkStarFill className="mt-1 text-primary" size={12} title={t('status.primary_code', 'Primary code')} />}
                  </div>
                );
              })()}
              <div className="min-w-0 flex flex-col gap-0">
                <div className="font-mono text-primary dark:text-primary font-semibold flex items-start gap-2 leading-none">
                  <span>{code.code}</span>
                </div>
                {(title || shouldShowSkeleton) && (
                  <div className="mt-0.5 pt-0.5 border-t border-border/40">
                    {title ? (
                      <div className="text-xs text-foreground/90 dark:text-foreground/90 whitespace-normal break-words">{title}</div>
                    ) : (
                      <Skeleton height={10} width={180} className="rounded-sm" {...skeletonTheme} />
                    )}
                  </div>
                )}
              </div>
            </div>
          }
          headerClassName="bg-[#e8ecf1] hover:bg-[#eef3f8] dark:bg-muted/25 dark:hover:bg-muted/35 [&>button]:p-1.5 [&>button>svg]:h-5 [&>button>svg]:w-5"
          actions={
            <>
              {typeof code.confidence === 'number' && (
                (() => {
                  const pct = Math.max(0, Math.min(1, code.confidence));
                  let hue: number;
                  if (pct <= 0.5) {
                    hue = 30;
                  } else {
                    const t = (pct - 0.5) / 0.5;
                    hue = Math.round(30 + (140 - 30) * t);
                  }
                  const bg = `hsl(${hue} 70% 45%)`;
                  return (
                    <span
                      className="w-2 h-2 rounded-full shrink-0 self-start mt-1.5 ring-2 ring-background/80"
                      style={{ background: bg }}
                      aria-label={`${Math.round(pct * 100)}%`}
                      title={`${Math.round(pct * 100)}%`}
                    />
                  );
                })()
              )}
            </>
          }
        >
          <div className="text-muted-foreground text-sm p-3" onClick={(e) => e.stopPropagation()}>
            {(code.explanation || code.description) ? (
              <div className="mb-2">{code.explanation || code.description}</div>
            ) : null}
            {code.text_references && code.text_references.length > 0 && (
              <div className="text-xs text-muted-foreground">
                <div className="font-medium">{t('status.references', 'References:')}</div>
                <ul className="list-disc pl-5 mt-1">
                  {code.text_references.map((r: any, i: number) => (
                    <li key={i} className="break-words">{(r.text_snippet || r.text || '').trim()}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Collapsible>
      </motion.div>
    );
  };

  const renderPcsCode = (code: PcsFinalCode) => {
    const pcsCode = code.code7 || code.code4;
    const isActive = !!(pcsCode && hoveredPcsCodes && hoveredPcsCodes.includes(pcsCode));
    const isSelected = !!(pcsCode && selectedPcsCodes && selectedPcsCodes.includes(pcsCode));
    const outerCls = `group rounded-md overflow-hidden transition-colors duration-150 border-2 ${isSelected ? 'border-primary bg-card/95' : isActive ? 'border-primary bg-card/95' : 'border-border/70 bg-[#f4f7fb] dark:bg-muted/25'}`;
    const meta = getMetadataFor('pcs', pcsCode);
    const title = meta?.data?.display_label as string | undefined;
    const shouldShowSkeleton = isMetadataLoading && !title && !metadataError;
    return (
      <motion.div
        key={pcsCode || `${code.code7 || code.code4 || 'pcs'}-${String(title || '')}`}
        layoutId={pcsCode ? `pcs-${pcsCode}` : undefined}
        layout="position"
        transition={{ type: 'spring', stiffness: 320, damping: 30 }}
        className={outerCls}
        onMouseEnter={(e) => {
          if (!pcsCode) return;
          if (e.buttons === 1 || window.getSelection()?.type === 'Range') return;
          setHoveredPcsCodes([pcsCode]);
        }}
        onMouseLeave={(e) => {
          if (e.buttons === 1 || window.getSelection()?.type === 'Range') return;
          setHoveredPcsCodes(null);
        }}
        onClick={() => {
          if (!pcsCode) return;
          setSelectedPcsCodes((prev) => {
            const cur = prev || [];
            if (cur.includes(pcsCode)) {
              const next = cur.filter((c) => c !== pcsCode);
              return next.length > 0 ? next : null;
            }
            return Array.from(new Set([...cur, pcsCode]));
          });
        }}
      >
        <Collapsible
          title={
            <div className="grid grid-cols-[auto,1fr] gap-2 items-start min-w-0">
              {(() => {
                const key = pcsCode || '';
                const raw = key ? session.codeColors?.[key] : undefined;
                if (isCssColor(raw)) {
                  return (
                    <div className="flex flex-col items-center">
                      <span className="mt-0.5 inline-block w-2.5 h-2.5 rounded opacity-80" style={{ backgroundColor: raw }} />
                    </div>
                  );
                }
                if (raw && isTailwindClass(raw)) {
                  return (
                    <div className="flex flex-col items-center">
                      <span className={`mt-0.5 inline-block w-2.5 h-2.5 rounded opacity-80 ${raw}`} />
                    </div>
                  );
                }

                const fallback = fallbackCssColorForCode(key);
                return (
                  <div className="flex flex-col items-center">
                    <span className="mt-0.5 inline-block w-2.5 h-2.5 rounded opacity-80" style={fallback ? { backgroundColor: fallback } : undefined} />
                  </div>
                );
              })()}
              <div className="min-w-0 flex flex-col gap-0">
                <span className="font-mono text-primary dark:text-primary font-semibold leading-none">{code.code7 || code.code4}</span>
                {(title || shouldShowSkeleton) && (
                  <div className="mt-0.5 pt-0.5 border-t border-border/40">
                    {title ? (
                      <div className="text-xs text-foreground/90 dark:text-foreground/90 whitespace-normal break-words">{title}</div>
                    ) : (
                      <Skeleton height={10} width={160} className="rounded-sm" {...skeletonTheme} />
                    )}
                  </div>
                )}
              </div>
            </div>
          }
          headerClassName="bg-[#f7f9fc] hover:bg-[#eef3f8] dark:bg-muted/25 dark:hover:bg-muted/35 [&>button]:p-1.5 [&>button>svg]:h-5 [&>button>svg]:w-5"
          actions={
            <>
              {typeof code.confidence === 'number' && (
                (() => {
                  const pct = Math.max(0, Math.min(1, code.confidence ?? 0));
                  let hue: number;
                  if (pct <= 0.5) {
                    hue = 30;
                  } else {
                    const t = (pct - 0.5) / 0.5;
                    hue = Math.round(30 + (140 - 30) * t);
                  }
                  const bg = `hsl(${hue} 70% 45%)`;
                  return (
                    <span
                      className="w-2 h-2 rounded-full shrink-0 self-start mt-1.5 ring-2 ring-background/80"
                      style={{ background: bg }}
                      aria-label={`${Math.round(pct * 100)}%`}
                      title={`${Math.round(pct * 100)}%`}
                    />
                  );
                })()
              )}
            </>
          }
        >
          <div className="text-sm text-muted-foreground p-3 space-y-2" onClick={(e) => e.stopPropagation()}>
            {code.rationale && (
              <p className="text-sm whitespace-pre-wrap">{code.rationale}</p>
            )}
            <div className="grid gap-2 sm:grid-cols-2">
              {[{ label: t('status.pcs_detail_approach', 'Approach'), value: code.approach }, { label: t('status.pcs_detail_device', 'Device'), value: code.device }, { label: t('status.pcs_detail_qualifier', 'Qualifier'), value: code.qualifier }, { label: t('status.pcs_detail_source', 'Source'), value: code.source }]
                .filter(detail => detail.value)
                .map(detail => (
                  <div key={detail.label} className="text-xs">
                    <span className="font-semibold text-foreground/80 mr-1">{detail.label}:</span>
                    <span>{detail.value}</span>
                  </div>
                ))}
            </div>

            {Array.isArray((code as any).text_references) && (code as any).text_references.length > 0 && (
              <div className="text-xs text-muted-foreground">
                <div className="font-medium">{t('status.references', 'References:')}</div>
                <ul className="list-disc pl-5 mt-1">
                  {(code as any).text_references.map((r: any, i: number) => (
                    <li key={i} className="break-words">{String((r?.text_snippet || r?.text || '')).trim()}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Collapsible>
      </motion.div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="lg:flex lg:items-start lg:gap-6">
        <div className="flex-1">
          <Card>
            <CardHeader className="pb-3 border-b border-border/60">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-2xl font-bold leading-tight">
                    {t('home.results.title', 'Extraction Results')}
                  </CardTitle>
                  {!showOriginalText && processedText && (
                    <Badge variant="secondary" className="text-xs">
                      {prunedText ? t('status.text_pruned', 'Pruned') : t('status.text_summarized', 'Summarized')}
                    </Badge>
                  )}
                </div>

                {pcsRan && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground hidden sm:inline-block">{t('status.highlight_mode', 'Highlight:')}</span>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        size="sm"
                        className="h-7 px-3 text-xs"
                        variant={highlightMode === 'diagnosis' ? 'default' : 'outline'}
                        onClick={() => {
                          setHighlightMode('diagnosis');
                          setIsDiagnosisListOpen(true);
                        }}
                      >
                        {t('status.highlight_mode_diagnosis', 'Diagnosis')}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        className="h-7 px-3 text-xs"
                        variant={highlightMode === 'pcs' ? 'default' : 'outline'}
                        onClick={() => {
                          setHighlightMode('pcs');
                          setIsDiagnosisListOpen(false);
                          setIsPcsListOpen(true);
                        }}
                      >
                        {t('status.highlight_mode_pcs', 'PCS')}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        className="h-7 px-3 text-xs"
                        variant={highlightMode === 'both' ? 'default' : 'outline'}
                        onClick={() => {
                          setHighlightMode('both');
                          setIsDiagnosisListOpen(true);
                          setIsPcsListOpen(true);
                        }}
                      >
                        {t('status.highlight_mode_both', 'Both')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <ColoredHighlightedText
                text={highlightText}
                codes={codesForHighlights}
                codeColors={session.codeColors}
                hideIrrelevant={hideIrrelevant}
                hoveredCodes={highlightHoveredCodes}
                onHighlightHover={onHighlightHoverSplit}
                onHighlightClick={handleHighlightClick}
                selectedCodes={highlightSelectedCodes}
                enableMarkdown={enableMarkdown}
              />

              {hasProcessedText && (
                <div className="mt-2 flex items-center gap-2">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={showOriginalText}
                      onCheckedChange={(value) => setShowOriginalText(!!value)}
                    />
                    {t('status.show_original_text', 'Show original text')}
                  </label>
                </div>
              )}

              {showOriginalText && (
                <div className="mt-4 flex items-center gap-2">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={hideIrrelevant}
                      onCheckedChange={setHideIrrelevant}
                    />
                    {t('status.hide_irrelevant', 'Hide irrelevant text')}
                  </label>
                </div>
              )}
              <div className="mt-2 flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowRawProcessedText((prev) => !prev)}
                  disabled={!rawProcessedText}
                >
                  {showRawProcessedText ? t('status.hide_raw_processed_text', 'Hide raw processed text') : t('status.show_raw_processed_text', 'Show raw processed text (debug)')}
                </Button>
              </div>
              {showRawProcessedText && rawProcessedText && (
                <div className="mt-3 rounded-md border border-border/60 bg-muted/20 p-3 text-xs font-mono whitespace-pre-wrap">
                  {rawProcessedText}
                </div>
              )}
            </CardContent>
          </Card>
          
          <Card className="mt-4 text-xs text-muted-foreground">
            <CardContent>
              <div className="mt-4 text-xs text-muted-foreground">
                <label className="text-sm font-medium">{t('form.user_feedback', 'Feedback (optional)')}</label>
                <Textarea
                  className="w-full rounded-md border border-border p-2 min-h-[80px] text-sm"
                  value={localFeedback || ''}
                  onChange={(e) => setLocalFeedback(e.target.value)}
                  placeholder={t('form.feedback_placeholder', 'Any notes or feedback about this extraction')}
                />
                
                <div className="flex gap-2 mt-4">
                  <Button
                    type="button"
                    onClick={async () => {
                      if (!session || !session.id) return;
                      try {
                        setSavingFeedback(true);
                        await extractions.updateSavedFeedback(session.id, localFeedback || '');
                      } catch (e) {
                        console.error('Failed to save feedback', e);
                      } finally {
                        setSavingFeedback(false);
                      }
                    }}
                    disabled={savingFeedback}
                    className="px-3 py-1 w-full"
                  >
                    {savingFeedback ? (
                      <div className="flex items-center justify-center gap-2">
                        <Spinner variant="circle" size={16} />
                        <span>{t('status.saving', 'Saving...')}</span>
                      </div>
                    ) : (
                      t('status.save', 'Save')
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        

        {/* Sidebar / Results Summary */}
        <div className="mt-6 lg:mt-0 lg:w-80">
          <Card className="">
            <CardHeader className="pb-3">
              <CardTitle>{t('home.results.summary', 'Results Summary')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('status.modes', 'Modes:')}</span>
                  <span className="font-medium">{modeSummary}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('status.codes_found', 'Codes Found:')}</span>
                  <Badge variant="secondary">{totalCodesFound}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('status.processing_time', 'Processing Time:')}</span>
                  <span className="font-medium">{results?.processing_time?.toFixed(2) ?? '0.00'}s</span>
                </div>
                { (results as any)?.costs?.total && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('status.total_tokens', 'Total Tokens:')}</span>
                      <span className="font-medium">{(results as any).costs.total.total_tokens}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('status.input_tokens', 'Input Tokens:')}</span>
                      <span>{(results as any).costs.total.input_tokens}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('status.output_tokens', 'Output Tokens:')}</span>
                      <span>{(results as any).costs.total.output_tokens}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('status.estimated_cost', 'Est. Cost:')}</span>
                      <Badge variant="outline">${(results as any).costs.total.estimated_cost_usd.toFixed(6)}</Badge>
                    </div>
                  </>
                )}
                {results?.metadata?.confidence_score && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('status.confidence', 'Confidence:')}</span>
                    {(() => {
                      const pct = Math.max(0, Math.min(1, results.metadata!.confidence_score));
                      const hue = Math.round(30 + (140 - 30) * pct);
                      const bg = `hsl(${hue} 70% 45%)`;
                      const textColor = '#fff';
                      return <Badge variant="success" style={{ background: bg, color: textColor, borderColor: 'transparent' }}>{(pct * 100).toFixed(1)}%</Badge>;
                    })()}
                  </div>
                )}
              </div>

              <div className="flex">
                <button
                  type="button"
                  className="w-full inline-flex items-center gap-2 text-sm text-muted-foreground bg-muted/10 border border-border rounded-full px-3 py-1 hover:bg-muted/20 transition-colors"
                  onClick={() => {
                    const diagnosticos = (results?.codes || [])
                      .map((c: any) => c.code)
                      .filter((code): code is string => !!code);
                    const procedimientos = pcsFinalCodes
                      .map((code: PcsFinalCode) => code.code7 || code.code4)
                      .filter((code): code is string => !!code);
                    if (diagnosticos.length === 0 && procedimientos.length === 0) return;
                    copyToClipboard(JSON.stringify({ diagnosticos, procedimientos }));
                  }}
                >
                  <CopyIcon className="w-4 h-4" />
                  {t('status.copy_all_codes', 'Copy All Codes')}
                </button>
              </div>

              {(results?.codes || []).length > 0 && (
                <AnimatedCollapsibleSection
                  open={isDiagnosisListOpen}
                  onOpenChange={setIsDiagnosisListOpen}
                  title={
                    <div className="flex items-center justify-between gap-2 min-w-0">
                      <span className="truncate">{t('status.icd10_codes', 'ICD-10 Codes:')}</span>
                      <Badge variant="secondary" className="shrink-0">{(results?.codes || []).length}</Badge>
                    </div>
                  }
                >
                  <LayoutGroup>
                    <div className="space-y-2">
                      {pinnedCodeList.length > 0 && (
                        <div className="sticky top-2 z-10 -mx-2 px-2 pt-2 pb-1 bg-transparent">
                          <div className="space-y-2 bg-background/95">
                            {pinnedCodeList.map(renderCodeCard)}
                          </div>
                        </div>
                      )}
                      <div className="space-y-2">
                        {unpinnedCodeList.map(renderCodeCard)}
                      </div>
                    </div>
                  </LayoutGroup>

                  {(results as any)?.costs?.steps?.length ? (
                    <div className="mt-6">
                      <h5 className="font-medium mb-2 text-sm uppercase tracking-wide text-muted-foreground">{t('status.cost_breakdown', 'Cost Breakdown')}</h5>
                      <div className="space-y-1 max-h-40 overflow-auto pr-1 text-xs">
                        {(results as any).costs.steps.map((s: any, i: number) => (
                          <div key={i} className="flex justify-between border-b border-border/40 py-0.5">
                            <span className="truncate mr-2">{s.step}</span>
                            <span className="text-muted-foreground mr-2">{s.total_tokens}</span>
                            <span className="font-mono">${s.estimated_cost_usd.toFixed(6)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </AnimatedCollapsibleSection>
              )}

              {pcsRan && (
                <div className="mt-4">
                  <AnimatedCollapsibleSection
                    open={isPcsListOpen}
                    onOpenChange={setIsPcsListOpen}
                    title={
                      <div className="flex items-center justify-between gap-2 min-w-0">
                        <span className="truncate">{t('status.pcs_codes_header', 'ICD-10-PCS Codes')}</span>
                        <Badge variant="secondary" className="shrink-0">{pcsFinalCodes.length}</Badge>
                      </div>
                    }
                  >
                    {pcsFinalCodes.length > 0 ? (
                      <LayoutGroup>
                        <div className="space-y-2">
                          {pinnedPcsCodeList.length > 0 && (
                            <div className="sticky top-2 z-10 -mx-2 px-2 pt-2 pb-1 bg-transparent">
                              <div className="space-y-2 bg-background/95">
                                {pinnedPcsCodeList.map(renderPcsCode)}
                              </div>
                            </div>
                          )}
                          <div className="space-y-2">
                            {unpinnedPcsCodeList.map(renderPcsCode)}
                          </div>
                        </div>
                      </LayoutGroup>
                    ) : (
                      <p className="text-sm text-muted-foreground">{t('status.pcs_none', 'No procedure codes were finalized by the PCS agent.')}</p>
                    )}
                  </AnimatedCollapsibleSection>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ResultsContent;
